import {createAsyncThunk} from '@reduxjs/toolkit';
import {MapNode} from 'common/src/modules/nodes/nodes.types';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {PatchTreeParams} from 'common/src/modules/campaignVersions/campaignVersions.api';
import getCampaignNodes from 'common/src/modules/nodes/utils/getCampaignNodes';
import {RootState} from 'common/src/redux/store';
import {updateCampaignNodes} from './updateCampaignNodes';
import {CAMPAIGN_NODE_ID, NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';
import campaignsApi from '../../../campaigns.api';

/**
 * Deletes a card from the campaign.
 * @param props - The properties of the card to delete.
 * @param dispatch - The dispatch function.
 * @param getState - The getState function.
 * @returns The response from the API.
 * @description This thunk deletes a card from the campaign.
 * It gets the campaign and version number from the state,
 * calls the API to delete the node,
 * and updates the nodesRef and redux state with the new nodes.
 */
export const deleteCard = createAsyncThunk(
  `${BreadcrumbName.CampaignLogic}/deleteCard`,
  async (
    props: {nodeToDelete: MapNode; campaignTreeType: CampaignTreeType},
    {dispatch, getState},
  ) => {
    const {nodeToDelete, campaignTreeType} = props;

    if (!nodeToDelete) return Promise.resolve();

    // Get campaign data from Redux state
    const state = getState() as RootState;
    const campaign = state.campaignPage.campaignTree.campaign;
    const versionNumber = state.campaignPage.campaignTree.versionNumber;
    const nodes = state.campaignPage.campaignLogic.nodes[campaignTreeType];

    if (!campaign || !versionNumber) {
      throw new Error('Campaign or version number not found in state');
    }

    // Determine current root node id from the campaign node output (if available)
    const campaignNode = nodes[CAMPAIGN_NODE_ID];
    const currentRootNodeId = Array.isArray(campaignNode.outputs)
      ? campaignNode.outputs[0].nodeId
      : undefined;

    // Call API to delete the node (similar to updateTree)
    const basePayload: PatchTreeParams = {
      campaignId: campaign.id,
      versionNumber,
      nodesToDelete: [nodeToDelete],
      nodesToAdd: [],
      nodesToUpdate: [],
      campaignNode: campaign,
      ...((nodeToDelete as DTCreativeNode)?.isFallbackCreative && {
        fallbackCreativeNodeId: NO_NODE_ID,
      }),
    };

    // If deleting the current root node, set rootNodeId to CAMPAIGN_NODE_ID
    if (currentRootNodeId && nodeToDelete.id === currentRootNodeId) {
      basePayload.rootNodeId = NO_NODE_ID;
    }

    const response = await campaignsApi.updateTree(basePayload);

    // Get updated nodes from response and update nodesRef
    const campaignNodes = getCampaignNodes(response);
    dispatch(updateCampaignNodes({connectedNodes: campaignNodes, campaignTreeType}));
  },
);
